import * as React from "react"
import moment from "moment"
import styled from "styled-components"
import includes from "lodash.includes"
import { FetchSingleListingUnits } from "../../generated/graphql"
import Row from "../row"
import Column from "../column"
import Text from "../text"
import Colors from "../../theme/colors"
import Tag from "../tag"
import Grid from "../grid"
import Gallery from "../gallery"
import { Unit_Amenities } from "../../constants/unit-amenities.constant"
import { BEDROOM_LABELS } from "../../constants/bedrooms-labels.constants"
import { ParkingLabels } from "../../constants/parking-labels.constants"
import ProgressiveImage from "react-progressive-bg-image"
import { DEFAULT_SPACE_PICTURE } from "src/config/constants"
import { Unit_Appliances } from "../../constants/unit-appliances.constants"

// const CAMERA_ICON = "/images/camera-icon.svg"

const RowWrapper = styled(Row as any)`
  justify-content: space-between;
  @media (max-width: 500px) {
    flex-direction: column;
  }
`

const LeftSide = styled(Column as any)`
  max-width: 510px;
  width: 100%;
`

const TagsWrapper = styled(Row as any)`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 24px;
`

const RightSide = styled(Column as any)`
  text-align: right;
  align-items: flex-end;
  @media (max-width: 500px) {
    align-items: flex-start;
    margin-bottom: 19px;
  }
`
const UnitImage = styled(ProgressiveImage)`
width: 132px;
height: 89px;
background-image: url("${props => props.src}");
background-size: cover;
background-repeat: no-repeat;
opacity: 1;
transition: 0.3s ease-in-out;
backface-visibility: hidden;
`

const Overlay = styled.div`
  transition: 0.3s ease-in-out;
  opacity: 0;
  position: absolute;
  display: block;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
  font-weight: 600;
  background-color: grey;
`

const ImageContainer = styled.div`
  position: relative;
  &:hover {
    ${UnitImage} {
      opacity: 0.3;
      cursor: pointer;
    }
    ${Overlay} {
      opacity: 1;
      cursor: pointer;
    }
  }
`

// const CameraIcon = styled.img`
//   width: 100%;
//   height: 100%;
// `

const FloorplanLink = styled.a`
  color: ${Colors.blue};
  text-decoration: none;
  &:hover {
    color: ${Colors.blue};
    text-decoration: underline;
    cursor: pointer;
  }
`

type Props = {
  unit?: FetchSingleListingUnits | null
}

// interface ImageProps {
//   backgroundImage?: string | null | false
// }

interface State {
  showGallery: boolean
}

class SingleListingUnitDisplay extends React.PureComponent<Props, State> {
  state = {
    showGallery: false
  }

  handleGalleryClick = () => this.setState({ showGallery: true })

  render() {
    const { unit } = this.props
    let _amenities
    let hasAtLeastOne: boolean = false
    let unitAppl

    if (unit && unit.unitAppliances !== null && unit.unitAppliances !== undefined) {
      unitAppl = unit && (Object as any).keys(unit.unitAppliances)
      let _values = (Object as any).values(unit.unitAppliances)
      hasAtLeastOne = includes(_values, true)
    }

    if (unit && unit.unitAmenities !== null && unit.unitAmenities !== undefined) {
      _amenities = unit && (Object as any).keys(unit.unitAmenities)
      let _values = (Object as any).values(unit.unitAmenities)
      hasAtLeastOne = includes(_values, true)
    }

    return (
      <RowWrapper>
        <LeftSide>
          <Text color={Colors.textLightGrey} fontSize={14} fontWeight={600}>
            Unit {unit && unit.number}
          </Text>
          <Text color={Colors.black} fontSize={18} fontWeight={600} mb="4px">
            {unit && unit.title}
          </Text>
          <TagsWrapper>
            {unit && unit.den !== 0 && <Tag>Den</Tag>}
            {unit && unit.unitSize && unit.unitSize > 0 ? (
              <Tag>Size: {unit.unitSize.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} sq ft</Tag>
            ) : null}
            {unit && unit.bedrooms && <Tag>Bedrooms: {BEDROOM_LABELS[unit.bedrooms]}</Tag>}
            {unit && unit.bathrooms && <Tag>Bathrooms: {unit && unit.bathrooms}</Tag>}
            {unit && <Tag>{unit.furnished ? "Furnished" : "Unfurnished"}</Tag>}
            {unit && <Tag>Parking Spots: {unit.parkingSpots ? unit.parkingSpots : "None"}</Tag>}
            {unit && unit.parking && (
              <Tag>{`Parking: ${ParkingLabels[unit.parking] ||
                unit.parking.toLocaleLowerCase()}`}</Tag>
            )}
            {unit && unit.deposit && unit.deposit > 0 ? (
              <Tag>
                Deposit:
                {" $"}
                {unit.deposit.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              </Tag>
            ) : null}
          </TagsWrapper>

          <Text color={Colors.darkGrey} fontSize={14} fontWeight={600} mb="8px">
            Unit Appliances
          </Text>

          <Grid marginBottom={19} smallerMobileRequired={true}>
            {unitAppl?.map((key: string) => {
              if (unit && unit.unitAppliances && unit.unitAppliances[key] === true) {
                let unitApp = Unit_Appliances.filter(elem => key === elem.value)[0]
                return (
                  <Text
                    fontSize={14}
                    color={Colors.darkGrey}
                    lineHeight={1.4}
                    key={"unitAppliances-" + key}
                    mb={0}
                  >
                    {unitApp.name}
                  </Text>
                )
              } else {
                return null
              }
            })}
            {!hasAtLeastOne && (
              <Text fontSize={14} color={Colors.darkGrey}>
                Please inquire about details
              </Text>
            )}
          </Grid>

          <Text color={Colors.darkGrey} fontSize={14} fontWeight={600} mt="24px">
            Unit Amenities
          </Text>
          <Grid marginBottom={19} smallerMobileRequired={true}>
            {_amenities.map((key: string) => {
              if (unit && unit.unitAmenities && unit.unitAmenities[key] === true) {
                let amenity = Unit_Amenities.filter(elem => key === elem.value)[0]
                return (
                  <Text
                    fontSize={14}
                    color={Colors.darkGrey}
                    lineHeight={1.4}
                    key={"amenities-" + key}
                    mb={0}
                  >
                    {amenity.name}
                  </Text>
                )
              } else {
                return null
              }
            })}
            {!hasAtLeastOne && (
              <Text fontSize={14} color={Colors.darkGrey}>
                Please inquire about details
              </Text>
            )}
          </Grid>
        </LeftSide>
        <RightSide>
          {unit && unit.available ? (
            <Text color={Colors.primary} fontSize={14} fontWeight={600}>
              {unit && moment.unix(unit.startDateAvailability / 1000).isAfter()
                ? "Available on " +
                moment.unix(unit.startDateAvailability / 1000).format("MMM DD YYYY")
                : "Available"}
            </Text>
          ) : (
            <Text color={Colors.fadedGrey}>Not Available</Text>
          )}
          <Text fontSize={18} color={Colors.darkGrey} mb="16px">
            {unit && unit.monthlyPrice !== 0 ? (
              <>
                ${unit.monthlyPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}/mo
              </>
            ) : (
              "Contact for Pricing!"
            )}
          </Text>
          {/* <Text fontSize={18} color={Colors.darkGrey} mb="16px">
            {"$"}
            {unit && unit.monthlyPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            {"/mo"}
          </Text> */}
          <ImageContainer onClick={this.handleGalleryClick}>
            <UnitImage
              src={unit && unit.pictures && unit.pictures.length > 0 && unit.pictures[0].url}
              placeholder={DEFAULT_SPACE_PICTURE}
            />
            <Overlay>{unit.pictures.length} Photos</Overlay>
            <Gallery
              visible={this.state.showGallery}
              images={
                unit && unit.pictures && unit.pictures.length > 0
                  ? unit.pictures
                  : [{ url: DEFAULT_SPACE_PICTURE }]
              }
              onClose={() => this.setState({ showGallery: false })}
            />
          </ImageContainer>
          {unit && unit.floorplan && (
            <Text mt="8px" lineHeight={1.4}>
              <FloorplanLink href={unit.floorplan} target="_blank">
                View Floor Plan
              </FloorplanLink>
            </Text>
          )}
        </RightSide>
      </RowWrapper>
    )
  }
}

export default SingleListingUnitDisplay
